import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuSmartphone, LuCloud, LuCheck } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../landing.css';

const SELF = [
  'Use your own SIM card and Android phone',
  '$0.001 per message — your only cost is the SIM\'s monthly plan',
  'No 10DLC or A2P registration',
  '5-minute setup, sub-$100 used Android works',
  'Best for high-volume marketing, GHL, dispatch, support',
];

const HOSTED = [
  'No phone or SIM required — we handle delivery',
  'Pay-per-message via aggregated carriers',
  'Instant signup, no device pairing',
  'Pick sender country per message',
  'Best for transactional one-offs and quick tests',
];

export default function ServicesOverview() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS Gateway Services Overview — APIs, Bulk SMS, GHL"
        description="Two ways to send SMS through TextLink: bring your own SIM ($0.001/msg, no 10DLC) or use our hosted gateway (instant, no device required). Same APIs, same dashboard, your choice."
        canonical="/smsservicesoverview"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Services overview</span>
            <h1 className="lp-h1">
              Two ways to send.<br />
              <span className="lp-h1-accent">Same APIs. Same dashboard.</span>
            </h1>
            <p className="lp-lead">
              Pair a spare Android with your own SIM for the lowest price-per-SMS on the market — or use our hosted gateway for instant delivery without any hardware. Both paths share one set of tools.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
            <div className="lp-meta">
              <span>300 free messages</span>
              <span className="lp-meta-sep" />
              <span>No credit card</span>
              <span className="lp-meta-sep" />
              <span>Switch services anytime</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">The two service types</h2>
            <p className="lp-sub">Both work with every TextLink tool. Mix and match per project.</p>

            <div className="lp-grid-2">
              <Link to="/selfhostedservice" className="lp-card">
                <div className="lp-card-icon"><LuSmartphone size={18} strokeWidth={2} /></div>
                <h3 className="lp-card-h">Use your own SIM</h3>
                <p className="lp-card-p">Pair a spare Android phone with a regular SIM. Send P2P messages at the SIM's plan rate.</p>
                <ul className="lp-feat-list" style={{ marginTop: 14, gridTemplateColumns: '1fr', gap: 8 }}>
                  {SELF.map((s, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13.5, color: 'var(--lp-text-muted)' }}>
                      <LuCheck size={14} strokeWidth={2.5} style={{ color: 'var(--lp-good)', flexShrink: 0, marginTop: 3 }} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <span className="lp-card-link" style={{ marginTop: 16 }}>
                  How self-hosted works <LuArrowRight size={13} strokeWidth={2} />
                </span>
              </Link>

              <Link to="/textlinkservice" className="lp-card">
                <div className="lp-card-icon"><LuCloud size={18} strokeWidth={2} /></div>
                <h3 className="lp-card-h">Hosted TextLink service</h3>
                <p className="lp-card-p">No device required. We deliver through our own carrier connections, pay-as-you-send.</p>
                <ul className="lp-feat-list" style={{ marginTop: 14, gridTemplateColumns: '1fr', gap: 8 }}>
                  {HOSTED.map((s, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13.5, color: 'var(--lp-text-muted)' }}>
                      <LuCheck size={14} strokeWidth={2.5} style={{ color: 'var(--lp-good)', flexShrink: 0, marginTop: 3 }} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <span className="lp-card-link" style={{ marginTop: 16 }}>
                  How hosted works <LuArrowRight size={13} strokeWidth={2} />
                </span>
              </Link>
            </div>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Same toolkit on both</h2>
            <p className="lp-sub">All five surfaces work regardless of which service backs them.</p>
            <div className="lp-grid-3">
              <Link to="/smsapi" className="lp-card">
                <h3 className="lp-card-h">SMS API</h3>
                <p className="lp-card-p">REST send + status webhooks.</p>
              </Link>
              <Link to="/bulksmsapi" className="lp-card">
                <h3 className="lp-card-h">Bulk SMS API</h3>
                <p className="lp-card-p">Array-based mass send.</p>
              </Link>
              <Link to="/verificationapi" className="lp-card">
                <h3 className="lp-card-h">Verification API</h3>
                <p className="lp-card-p">OTP / 2FA over SMS.</p>
              </Link>
              <Link to="/bulksms" className="lp-card">
                <h3 className="lp-card-h">No-code Bulk SMS</h3>
                <p className="lp-card-p">CSV upload, template, send.</p>
              </Link>
              <Link to="/web-sms-app" className="lp-card">
                <h3 className="lp-card-h">Web Texting App</h3>
                <p className="lp-card-p">Browser inbox + threads.</p>
              </Link>
              <Link to="/ghl" className="lp-card">
                <h3 className="lp-card-h">GoHighLevel</h3>
                <p className="lp-card-p">Native conversation provider.</p>
              </Link>
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Not sure which service?</h2>
            <p className="lp-sub">Most agencies and SaaS founders go self-hosted for the price. Sign up free, try both.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/Contact">Ask us</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
